/**
 * Maris Desk public homepage — mounted at /home.
 *
 * 2026-06-23 editorial refinement: dropped the SaaS card pattern in favor
 * of a hairline-and-margin-note layout. Reads like an operations notebook,
 * not a feature page. Adds a founder-voice opening note and a "Notes from
 * the guest desk" section in place of generic pain-point cards. The 5-step
 * flow is now a single hairline list, not a card grid.
 *
 * No app, lead, client, or showcase data is rendered. Primary CTA →
 * /request-review. Secondary CTA → /desk/login (Existing Client Login).
 * No public signup pathway anywhere on the page.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight } from 'lucide-react';

const REQUEST_REVIEW_PATH = '/request-review';
const LOGIN_PATH = '/desk/login';
const DEMO_VIDEO_URL =
  'https://customer-assets.emergentagent.com/job_f831c633-86da-4827-88d8-cd6fc8200a9b/artifacts/4du1qq3f_Maris%20Desk%20Demo_20260619_201645_0000.mp4';

/* ------------------------------------------------------------------ */
/* Small primitives                                                    */
/* ------------------------------------------------------------------ */

const Eyebrow = ({ children, className = '' }) => (
  <div className={`inline-flex items-center text-[10.5px] tracking-[0.22em] uppercase font-medium text-[#3F617E] ${className}`}>
    <span>{children}</span>
  </div>
);

/** Tiny operational tag — used as a left-margin label, not a feature pill. */
const FieldLabel = ({ children }) => (
  <span className="inline-flex items-center text-[10px] tracking-[0.18em] uppercase font-semibold text-[#3F617E]/85">
    {children}
  </span>
);

/* ------------------------------------------------------------------ */
/* Content                                                              */
/* ------------------------------------------------------------------ */

const DESK_NOTES = [
  { label: 'open loop',         line: 'A towel request gets answered, but no one confirms it was delivered.' },
  { label: 'no owner',          line: 'An A/C issue is mentioned during handover, but the next step has no owner.' },
  { label: 'not handed over',   line: 'Late checkout is approved verbally, but not visible to the next shift.' },
  { label: 'waiting',           line: 'A guest receives a reply, but no one tracks whether the issue was resolved.' },
  { label: 'discovered late',   line: 'A manager sees the complaint later, but not the request while it was still fixable.' },
];

const RHYTHM_STEPS = [
  { label: 'Intake',       body: 'Every guest request lands in one clear Guest Inbox. Desk agents or housekeepers log any incoming need in three taps on the mobile backend.' },
  { label: 'Assign',       body: 'Someone owns the next step. Tickets require an explicit human owner right away, completely eliminating the dangerous assumption that "someone else has it covered."' },
  { label: 'Follow up',    body: 'Open loops stay visible until they close. A built-in countdown tracker keeps tasks flagged on the dashboard until the floor staff explicitly verifies fulfillment.' },
  { label: 'Daily Run',    body: 'The shift works from one priority list, with carry-overs. Pending tasks from the morning crew lock automatically into the incoming shift\u2019s feed so no verbal promises are left behind.' },
  { label: 'Manager view', body: 'What is open, repeating, or at risk \u2014 while it is still fixable. Leadership gets silent, automated alerts for stalling tickets, allowing you to intercept friction before a guest checks out and leaves a 3-star review.' },
];

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

const Home = () => {
  return (
    <div className="min-h-screen bg-[#F3F5F7] text-stone-900 antialiased" data-testid="home-page">

      {/* ---------------------- Top nav ---------------------- */}
      <header className="border-b border-stone-200/70 bg-[#F3F5F7]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-14 flex items-center justify-between">
          <Link
            to="/home"
            className="flex items-center gap-2 group"
            data-testid="home-nav-logo"
          >
            <span className="font-heading text-[17px] sm:text-[19px] text-stone-900 group-hover:text-[#3F617E] transition-colors">
              Maris Desk
            </span>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to={LOGIN_PATH}>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full text-[11.5px] sm:text-xs px-3 h-8 text-stone-700 hover:text-stone-900 hover:bg-stone-100 font-medium tracking-wide"
                data-testid="home-nav-login"
              >
                Existing Client Login
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* ---------------------- Hero ---------------------- */}
      <section className="pt-10 sm:pt-12 md:pt-16 pb-8 md:pb-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <Eyebrow>For hotels, villas &amp; short-term rentals</Eyebrow>
          <h1
            className="font-heading text-[30px] sm:text-[38px] md:text-[46px] leading-[1.08] tracking-tight text-stone-900 mt-5"
            data-testid="home-hero-headline"
          >
            Guest requests move fast.
            <br className="hidden sm:block" />
            <span className="text-[#3F617E]"> Maris Desk </span>
            helps teams keep them visible.
          </h1>
          <p className="text-stone-600 leading-relaxed text-[15px] sm:text-[16.5px] mt-5 max-w-2xl">
            Maris Desk gives small hospitality teams one calm place to see what came in, who owns it,
            what still needs follow-up, and what should carry into the next shift.
          </p>
          <div className="mt-7 flex items-center flex-wrap gap-x-3 gap-y-3">
            <Link to={REQUEST_REVIEW_PATH}>
              <Button
                size="lg"
                className="bg-[#3F617E] hover:bg-[#345470] rounded-full px-5 sm:px-6 h-12 text-[14.5px] sm:text-[15px] shadow-md shadow-[#3F617E]/15 font-medium tracking-wide"
                data-testid="home-hero-primary-cta"
              >
                Request Your Guest Request Review Flow
                <ArrowRight className="h-4 w-4 ml-2 flex-shrink-0" />
              </Button>
            </Link>
            <Link to={LOGIN_PATH}>
              <Button
                size="lg"
                variant="ghost"
                className="rounded-full px-5 h-12 text-[14.5px] sm:text-[15px] text-stone-700 hover:text-stone-900 hover:bg-stone-100 font-medium"
                data-testid="home-hero-secondary-cta"
              >
                Existing Client Login
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-[11.5px] tracking-[0.08em] uppercase text-stone-500">
            Guided onboarding · Free flow review · Built for small hospitality teams
          </p>
        </div>
      </section>

      {/* ---------------------- Founder / operator note ---------------------- */}
      {/* Short editorial insight — the only "why the problem matters" beat on
       * the page. The previous POV section ("Not another inbox / A calm
       * operating layer") was removed in the 2026-06-23 tightening pass to
       * avoid restating the same idea in three places. */}
      <section className="pt-2 pb-10 md:pb-14" data-testid="home-founder-note">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <div className="border-l-2 border-[#3F617E]/30 pl-5 sm:pl-7 py-1">
            <FieldLabel>A note from the desk</FieldLabel>
            <p className="font-heading text-[17px] sm:text-[20px] md:text-[22px] leading-snug text-stone-800 mt-3">
              Most guest-service misses do not begin as big failures. They begin as small requests
              that were answered, but not clearly owned, followed up, or carried into the next shift.
              Maris Desk ensures every single request is owned, tracked, and flawlessly handed off.
            </p>
            <p className="text-[12px] tracking-[0.18em] uppercase text-stone-500 mt-4">
              — Maris Desk · Hospitality operations
            </p>
          </div>
        </div>
      </section>

      {/* ---------------------- Notes from the guest desk ---------------------- */}
      <section className="py-10 md:py-14 border-t border-stone-200/70" data-testid="home-pain-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <Eyebrow>Where teams quietly lose ground</Eyebrow>
          <h2 className="font-heading text-[24px] sm:text-[28px] md:text-[30px] leading-tight text-stone-900 mt-4">
            The Silent Leak in Boutique Operations
          </h2>
          <p className="text-stone-600 leading-relaxed text-[14.5px] mt-3 max-w-xl">
            Real observations from front-of-house and operations leads. Small misses, the kind
            that compound across a shift.
          </p>

          {/* Hairline-divided list — no cards, no shaded rows. Reads like
              a notebook page rather than a feature grid. */}
          <div className="mt-9 divide-y divide-stone-200/80 border-t border-stone-200/80">
            {DESK_NOTES.map((n, idx) => (
              <article
                key={idx}
                className="py-6 sm:py-7 grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-y-2 sm:gap-x-8 items-start"
                data-testid={`home-desk-note-${idx}`}
              >
                <div className="flex items-baseline gap-3">
                  <span className="text-[11px] font-mono text-stone-400 tabular-nums">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <FieldLabel>{n.label}</FieldLabel>
                </div>
                <p className="text-[15.5px] sm:text-[17px] leading-snug text-stone-800">
                  {n.line}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------- The daily rhythm ---------------------- */}
      <section className="py-10 md:py-14 border-t border-stone-200/70" data-testid="home-flow-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <Eyebrow>How Maris Desk works</Eyebrow>
          <h2 className="font-heading text-[24px] sm:text-[28px] md:text-[30px] leading-tight text-stone-900 mt-4">
            The daily rhythm Maris Desk protects
          </h2>
          <p className="text-stone-600 leading-relaxed text-[14.5px] mt-3 max-w-xl">
            Every request needs a digital place to land, an explicitly assigned team member to own
            it, and an automated system that keeps it visible until it is verified closed or
            securely carried forward.
          </p>

          {/* Vertical numbered list — same hairline rhythm as the desk notes. */}
          <ol className="mt-9 divide-y divide-stone-200/80 border-t border-stone-200/80">
            {RHYTHM_STEPS.map((s, idx) => (
              <li
                key={s.label}
                className="py-5 sm:py-6 grid grid-cols-[40px_1fr] sm:grid-cols-[64px_140px_1fr] gap-y-1 sm:gap-x-8 items-baseline"
                data-testid={`home-rhythm-step-${idx}`}
              >
                <span className="font-heading text-[24px] sm:text-[28px] text-stone-300 tabular-nums leading-none">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <p className="text-[11.5px] tracking-[0.16em] uppercase font-semibold text-[#3F617E]/90 sm:pt-1.5 col-start-2">
                  {s.label}
                </p>
                <p className="text-[14.5px] sm:text-[15.5px] leading-relaxed text-stone-700 col-span-2 sm:col-span-1 sm:col-start-3 sm:row-start-1 max-w-xl">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------------------- Final conversion anchor ---------------------- */}
      {/* Sits directly beneath step 05 "Manager view", above the Start Here /
       * GRFR section. Same hairline rhythm as the rest of the page — no card
       * shadow, brand accent on the CTA. Intent: convert a reader who's just
       * finished the daily-rhythm walk-through. */}
      <section
        className="py-10 md:py-14 border-t border-stone-200/70"
        data-testid="home-conversion-anchor"
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <h2
            className="font-heading text-[24px] sm:text-[28px] md:text-[30px] leading-tight text-stone-900"
            data-testid="home-conversion-anchor-title"
          >
            Bring a predictable operational rhythm to your front desk.
          </h2>
          <p className="text-stone-600 leading-relaxed text-[15px] sm:text-[16px] mt-4 max-w-2xl">
            Stop relying on staff memory to protect your 5-star reviews. Maris Desk acts as the
            silent operational backbone for your boutique team. Get configured in under 15 minutes.
          </p>

          <div className="mt-7">
            <Link to={REQUEST_REVIEW_PATH}>
              <Button
                size="lg"
                className="bg-[#3F617E] hover:bg-[#345470] rounded-full px-5 sm:px-6 h-12 text-[14.5px] sm:text-[15px] shadow-md shadow-[#3F617E]/15 max-w-full whitespace-normal sm:whitespace-nowrap text-left sm:text-center leading-snug font-medium"
                data-testid="home-founding-member-cta"
              >
                Claim Your Guest Flow Review
                <ArrowRight className="h-4 w-4 ml-2 flex-shrink-0" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------------- GRFR — the natural first step ---------------------- */}
      {/* Bridge to the standalone review intake. NOT a duplicate of the
       * canonical review page. Compressed in 2026-06-23 tightening pass:
       * the 3-column meta block collapsed into a single dot-separated line,
       * heading reframed as the call to start. */}
      <section className="py-10 md:py-14 border-t border-stone-200/70" data-testid="home-grfr-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <Eyebrow>Start here</Eyebrow>
          <h2 className="font-heading text-[24px] sm:text-[28px] md:text-[30px] leading-tight text-stone-900 mt-4">
            Start with a Guest Request Flow Review
          </h2>
          <p className="text-stone-600 leading-relaxed text-[15px] sm:text-[16px] mt-4 max-w-2xl">
            Before Maris Desk becomes a workspace, we start with the flow — where guest requests
            arrive, who owns them, how follow-ups happen, and what carries into the next shift.
          </p>

          <p className="mt-5 text-[11.5px] tracking-[0.14em] uppercase text-[#3F617E] font-semibold">
            Channels · Ownership · Follow-up · Handover
          </p>

          <div className="mt-7">
            <Link to={REQUEST_REVIEW_PATH}>
              <Button
                size="lg"
                className="bg-[#3F617E] hover:bg-[#345470] rounded-full px-5 sm:px-6 h-12 text-[14.5px] sm:text-[15px] shadow-md shadow-[#3F617E]/15 max-w-full whitespace-normal sm:whitespace-nowrap text-left sm:text-center leading-snug font-medium"
                data-testid="home-grfr-cta"
              >
                <span className="sm:hidden">Start My Free Flow Review</span>
                <span className="hidden sm:inline">Start My Free Flow Review</span>
                <ArrowRight className="h-4 w-4 ml-2 flex-shrink-0" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------------- Demo video ---------------------- */}
      <section className="py-10 md:py-14 border-t border-stone-200/70" data-testid="home-video-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <Eyebrow>Walkthrough</Eyebrow>
          <h2 className="font-heading text-[24px] sm:text-[28px] md:text-[30px] leading-tight text-stone-900 mt-4">
            See the guest desk in motion
          </h2>
          <p className="text-stone-600 leading-relaxed text-[14.5px] mt-3 max-w-xl">
            A short walkthrough of how requests move from Guest Inbox to Daily Run, follow-up, and
            manager view.
          </p>

          <div className="mt-9">
            {/* Restrained frame — no oversized card shadow. */}
            <div className="rounded-md border border-stone-200 bg-stone-950 aspect-video overflow-hidden">
              <video
                src={DEMO_VIDEO_URL}
                controls
                preload="metadata"
                playsInline
                className="w-full h-full object-contain"
                data-testid="home-demo-video-player"
              >
                Your browser does not support embedded video. Please use a modern browser to view
                the Maris Desk walkthrough.
              </video>
            </div>
            <p className="text-[11.5px] tracking-[0.12em] uppercase text-stone-500 mt-4">
              A short walkthrough of the Maris Desk flow
            </p>
          </div>
        </div>
      </section>

      {/* ---------------------- Already using Maris Desk ---------------------- */}
      <section className="py-10 md:py-12 border-t border-stone-200/70" data-testid="home-existing-users-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <p className="text-[14px] sm:text-[15px] text-stone-700 leading-relaxed max-w-xl">
            <span className="font-heading text-stone-900 text-[16px] sm:text-[17px]">Already using Maris Desk?</span>{' '}
            Sign in to your team workspace — Today, Daily Run, Guest Inbox, Follow-ups, and Reports.
          </p>
          <Link to={LOGIN_PATH}>
            <Button
              size="lg"
              className="bg-[#3F617E] hover:bg-[#345470] rounded-full px-5 sm:px-6 h-11 text-[14px] font-medium tracking-wide flex-shrink-0"
              data-testid="home-existing-users-login"
            >
              Existing Client Login
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ---------------------- Footer ---------------------- */}
      <footer className="border-t border-stone-200/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 sm:gap-4">
          <div className="flex items-center gap-3">
            <span className="font-heading text-base text-stone-900">Maris Desk</span>
            <span className="hidden sm:inline-flex text-[10.5px] tracking-[0.2em] uppercase text-stone-400">
              Calm hospitality operations
            </span>
          </div>
          <nav
            className="grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:items-center sm:gap-5 text-[13.5px] sm:text-[12.5px] text-stone-500"
            aria-label="Footer"
          >
            <Link to={REQUEST_REVIEW_PATH} className="hover:text-stone-900 transition-colors py-1 sm:py-0" data-testid="home-footer-request-review">
              Guest Request Flow Review
            </Link>
            <Link to={LOGIN_PATH} className="hover:text-stone-900 transition-colors py-1 sm:py-0" data-testid="home-footer-login">
              Existing Client Login
            </Link>
            <Link to="/privacy" className="hover:text-stone-900 transition-colors py-1 sm:py-0">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-stone-900 transition-colors py-1 sm:py-0">
              Terms
            </Link>
            <a
              href="mailto:support@marisdesk.com"
              className="hover:text-stone-900 transition-colors py-1 sm:py-0"
              data-testid="home-footer-contact"
            >
              Contact
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Home;
